import React, { useContext, useRef, useState } from 'react';
import { Header, Button, Dropdown, Icon, Ref } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { Table, TableLoader, EllipsisDropdown, Person, DateTimeUtils, EllipsisDropdownPortal, OnHoverOnClick } from 'dyl-components';
import masterAccountActions from 'actions/master_account';
import { StringUtils } from 'utils';
import {
    KEY_CREATED_ON, KEY_ACCOUNT_TYPE, KEY_OWNER,
    KEY_ACCOUNT_STATUS, KEY_ACTIONS,
    KEY_REACTIVATED_ON, KEY_CREATED_BY, KEY_REACTIVATED_BY,
    KEY_LAST_MODIFIED, KEY_FUND_TYPE,
    KEY_LAST_MODIFIED_BY, KEY_CUSTOMER_SINCE,
    KEY_CANCELED_ON, KEY_CANCELED_BY,
    KEY_CO_OWNER
} from './constants';
import { StatusDropdown } from './ConversionControls';
import { FormsContext } from './FormsProvider';
import Account from 'shared/Account';
import HoverContent from 'shared/hoverContent';
import RestrictedView from 'shared/RestrictedView';
import { USER_PERMISSIONS, multipleRestriction } from 'dyl-components';

const DropdownItem = ({ text, icon, ...otherProps }) => {
    return (
        <Dropdown.Item text={<React.Fragment>
            <Icon name={icon} />
            {text}
        </React.Fragment>}
            style={{ cursor: 'pointer' }}
            {...otherProps}
        />
    );
}
const createActionOptions = (
    isNested,
    account_type,
    onLinkSelect,
    onUnlinkSelect,
    onDeleteMasterAccountSelect
) => {
    const options = [];

    switch (account_type) {
        case 'Master account':
            options.push({
                text: 'Delete',
                icon: 'fa-solid fa-trash',
                onClick: onDeleteMasterAccountSelect,
                userPermission: USER_PERMISSIONS.MASTER_ACCOUNT_DELETE
            });
            break;
        case 'business':
            if (!isNested) {
                options.push({
                    text: 'Link Master Account',
                    icon: 'fas fa-diagram-nested',
                    onClick: onLinkSelect,
                    userPermission: USER_PERMISSIONS.MASTER_ACCOUNT_EDIT
                });
            } else {
                options.push({
                    text: 'Change Master Account',
                    icon: 'fa-solid fa-buildings',
                    onClick: onLinkSelect,
                    userPermission: USER_PERMISSIONS.MASTER_ACCOUNT_EDIT
                });
                options.push({
                    text: 'Unlink Master Account',
                    icon: 'fas fa-diagram-nested',
                    onClick: onUnlinkSelect,
                    userPermission: USER_PERMISSIONS.MASTER_ACCOUNT_EDIT
                });
            }
            break;
        default:
    }

    return options;
}

const Subrow = ({
    master_account,
    nested_account,
    isColumnHidden,
    getAccountStatus,

    onLinkSelect,
    onUnlinkSelect
}) => {
    const actionPopupRef = React.createRef();
    return (
        <Table.CollapsibleRowContent>
            <Table.Cell width={1} />
            {
                !isColumnHidden(KEY_CREATED_ON) &&
                <Table.Cell>
                    <div><b>{DateTimeUtils.formatEpoch(nested_account.created, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{DateTimeUtils.formatEpoch(nested_account.created, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_CREATED_BY) &&
                <Table.Cell>
                    {nested_account.created_by && (
                        <Person
                            username={nested_account.created_by.username}
                            email={nested_account.created_by.email}
                            isInactiveUser={!nested_account.created_by.active}
                        />
                    )}
                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_LAST_MODIFIED) &&
                <Table.Cell>
                    <div><b>{nested_account.activity && DateTimeUtils.formatEpoch(nested_account.activity, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{nested_account.activity && DateTimeUtils.formatEpoch(nested_account.activity, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_LAST_MODIFIED_BY) &&
                <Table.Cell>
                    {nested_account.last_modified_by && (
                        <Person 
                            username={nested_account.last_modified_by.username}
                            email={nested_account.last_modified_by.email}
                            isInactiveUser={!nested_account.last_modified_by.active}
                        />
                    )}
                </Table.Cell>
            }
            <Table.Cell>
                <Account
                    name={nested_account.name}
                    id={nested_account.id && `${nested_account.id}`}
                    linkToAccount={<Link to={`/account/${nested_account.id}`}>{nested_account.name}</Link>}
                    isActionsVisible
                    account={nested_account}
                />
            </Table.Cell>
            {
                !isColumnHidden(KEY_ACCOUNT_TYPE) &&
                <Table.Cell>
                    <b>{StringUtils.capitalize(nested_account.account_type)}</b>
                </Table.Cell>
            }
            {!isColumnHidden(KEY_OWNER) && (
                <Table.Cell>
                    {nested_account.customer?.owned_by ? <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={nested_account.customer.owned_by}
                            />
                        }
                        clickable={false}
                        trigger={nested_account.customer.owned_by ?
                            <div className='TableTruncateFixedWidth'>
                                <Person
                                    username={`${nested_account.customer.owned_by.first_name} ${nested_account.customer.owned_by.last_name}`}
                                    email={nested_account.customer.owned_by.email}
                                    popup={false}
                                    isInactiveUser={!nested_account.customer.owned_by.active}
                                    title={ " "}
                                />
                            </div>
                            : undefined}
                    /> : "Unassigned"}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CO_OWNER) && (
                <Table.Cell>
                    {nested_account.customer?.co_owned_by ? <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={nested_account.customer.co_owned_by}
                            />
                        }
                        clickable={false}
                        trigger={nested_account.customer.co_owned_by ?
                            <div className='TableTruncateFixedWidth'>
                                <Person
                                    username={`${nested_account.customer.co_owned_by.first_name} ${nested_account.customer.co_owned_by.last_name}`}
                                    email={nested_account.customer.co_owned_by.email}
                                    popup={false}
                                    isInactiveUser={!nested_account.customer.co_owned_by.active}
                                    title={" "}
                                />
                            </div>
                            : undefined}
                    /> : "Unassigned"}
                </Table.Cell>
            )}
            {
                !isColumnHidden(KEY_FUND_TYPE) && (
                    <Table.Cell>
                        <Header as='h5'>
                            {StringUtils.capitalize(nested_account.customer?.customer_fund_type)}
                        </Header>
                    </Table.Cell>
                )
            }
            {!isColumnHidden(KEY_CUSTOMER_SINCE) && (
                <Table.Cell>
                    <div><b>{nested_account?.customer?.customer_since && DateTimeUtils.formatEpoch(nested_account?.customer?.customer_since, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{nested_account?.customer?.customer_since && DateTimeUtils.formatEpoch(nested_account?.customer?.customer_since, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CANCELED_ON) && (
                <Table.Cell>
                    <div><b>{nested_account.customer.canceled_on && DateTimeUtils.formatEpoch(nested_account.customer.canceled_on, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{nested_account.customer.canceled_on && DateTimeUtils.formatEpoch(nested_account.customer.canceled_on, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CANCELED_BY) && (
                <Table.Cell>
                    {nested_account.customer.canceled_by && (
                        <Person
                            username={nested_account.customer.canceled_by.username}
                            email={nested_account.customer.canceled_by.email}
                            isInactiveUser={!nested_account.customer.canceled_by.active}
                        />
                    )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_REACTIVATED_ON) && (
                <Table.Cell>
                    <div><b>{nested_account.customer.reactivated_on && DateTimeUtils.formatEpoch(nested_account.customer.reactivated_on, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{nested_account.customer.reactivated_on && DateTimeUtils.formatEpoch(nested_account.customer.reactivated_on, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_REACTIVATED_BY) && (
                <Table.Cell>{nested_account.customer.reactivated_by && (
                    <Person
                        username={nested_account.customer.reactivated_by.username}
                        email={nested_account.customer.reactivated_by.email}
                        isInactiveUser={!nested_account.customer.reactivated_by.active}
                    />
                )}</Table.Cell>
            )}
            {!isColumnHidden(KEY_ACCOUNT_STATUS) &&
                <Table.Cell className='Account__status'>
                    {getAccountStatus({
                        id: nested_account.id,
                        account_type: nested_account.account_type,
                        account_status: nested_account.customer?.customer_status,
                        fund_type:nested_account?.customer?.customer_fund_type,
                        ...(nested_account.primary_contact.id && { primary_person_id: nested_account.primary_contact?.id })
                    })}
                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_ACTIONS) &&
                <Table.Cell>
                    <EllipsisDropdown>
                        {createActionOptions(
                            true,
                            nested_account.account_type,
                            () => { onLinkSelect(nested_account.id, master_account.id); },
                            () => {
                                onUnlinkSelect(
                                    actionPopupRef,
                                    { master_account: master_account.id, account_id: nested_account.id }
                                );
                            }
                        ).map(({ text, icon, userPermission, ...otherProps }, idx) =>
                            <Ref innerRef={actionPopupRef}>
                                <RestrictedView type="disabled" userPermission={userPermission}>
                                    <DropdownItem
                                        key={idx}
                                        text={text}
                                        icon={icon}
                                        {...otherProps}
                                    />
                                </RestrictedView>
                            </Ref>
                        )}
                    </EllipsisDropdown>
                </Table.Cell>
            }
        </Table.CollapsibleRowContent >
    )
}


export const AccountsRow = ({ account, isColumnHidden }) => {
    const actionPopupRef = React.createRef();
    const dispatch = useDispatch();
    const ref = useRef();
    const { userPermissions, isDyl } = useSelector((state) => {  
        return {
            userPermissions: state.role.userPermissions.permission,
            isDyl: state.auth.dyl_employee,
        }
    });
    const [params] = useSearchParams();

    const { onStatusDropdownSelect, onLinkSelect, onUnlinkSelect, onDeleteMasterAccountSelect } = useContext(FormsContext);
    const getAccountStatus = (account) => {
        const isMasterAccount = account.account_type === 'Master account';
        if (isMasterAccount) {
            return (
                <Button size='tiny' className='Accounts__hasAccountsButton' onClick={account.business_count ? onToggle : null}>
                    {`${account.account_status} Account${parseInt(account.account_status) !== 1 ? 's' : ''}`}
                </Button>
            )
        }

        return (
            (account.fund_type !== "unfunded" || !isDyl) && <StatusDropdown
                account={account}
                onStatusDropdownSelect={onStatusDropdownSelect}
            />
        )
    }

    const [collapsed, setCollapsed] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [isReadingAccounts, setIsReadingAccounts] = useState(false);

    const refreshAccounts = async () => {
        await setIsReadingAccounts(true);
        try {
            const response = await dispatch(masterAccountActions.readAccounts(account.id,{ fund_type: params.get('fund_type')?.split(",") , owner_id: params.get('owner_id')?.split(","), co_owner_id: params.get('co_owner_id')?.split(",")  }));
            await setAccounts(response.data);
        } catch (e) {
            console.log(e);
            await setAccounts([]);
        } finally {
            await setIsReadingAccounts(false);
        }
    }

    const hasNestedAccounts = account.account_type === 'Master account';
    const RowComponent = hasNestedAccounts ? Table.CollapsibleRow : Table.Row;

    const onToggle = () => {
        setCollapsed(!collapsed);
        if (!collapsed) {
            setAccounts([]);
        } else {
            refreshAccounts();
        }
    };

    return (
        <RowComponent
            onToggle={onToggle}
            collapsed={collapsed}
            toggleDisabled={!(Number(account.account_status) > 0) || multipleRestriction([USER_PERMISSIONS.ACCOUNT_VIEW], userPermissions)}
            subrowContent={(
                isReadingAccounts ? <TableLoader colspan={7} isLoading /> :
                    accounts?.map(nested_account =>
                        <Subrow
                            master_account={account}
                            nested_account={nested_account}
                            getAccountStatus={getAccountStatus}
                            isColumnHidden={isColumnHidden}
                            isReadingAccounts={isReadingAccounts}

                            onLinkSelect={onLinkSelect}
                            onUnlinkSelect={onUnlinkSelect}
                        />
                    )
            )}
        >
            {!hasNestedAccounts && <Table.Cell />}
            {
                !isColumnHidden(KEY_CREATED_ON) &&
                <Table.Cell>
                    <div><b>{DateTimeUtils.formatEpoch(account.created, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{DateTimeUtils.formatEpoch(account.created, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_CREATED_BY) &&
                <Table.Cell>
                    {account.created_by && <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={account.created_by}
                            />
                        }
                        clickable={false}
                        trigger={account.created_by ?
                            <div className='TableTruncateFixedWidth'>
                                <Person
                                    username={`${account.created_by.first_name} ${account.created_by.last_name}`}
                                    email={account.created_by.email}
                                    popup={false}
                                    isInactiveUser={!account.created_by.active}
                                    title={ " "}
                                />
                            </div>
                            : undefined}
                    />}
                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_LAST_MODIFIED) &&
                <Table.Cell>
                    {
                        account.created !== account.activity && (
                            <React.Fragment>
                                <div><b>{account.activity && DateTimeUtils.formatEpoch(account.activity, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                                <div>{account.activity && DateTimeUtils.formatEpoch(account.activity, DateTimeUtils.TIME_FORMAT)}</div>
                            </React.Fragment>
                        )
                    }
                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_LAST_MODIFIED_BY) &&
                <Table.Cell>
                    {account.last_modified_by && (
                        <Person 
                            username={account.last_modified_by.username}
                            email={account.last_modified_by.email}
                            isInactiveUser={!account.last_modified_by.active}
                        />
                    )}
                </Table.Cell>
            }
            <Table.Cell>
                <Account
                    name={account.name}
                    id={account.id}
                    account={account}
                    linkToAccount={
                        <Link to={`/${account.account_type === 'Master account' ? 'master_account' : 'account'}/${account.id}`}>{account.name}</Link>
                    }
                    isActionsVisible={account.account_type !== 'Master account'}
                />
            </Table.Cell>
            {
                !isColumnHidden(KEY_ACCOUNT_TYPE) &&
                <Table.Cell>
                    <b>{StringUtils.capitalize(account.account_type)}</b>
                </Table.Cell>
            }
            {!isColumnHidden(KEY_OWNER) && (
                <Table.Cell>
                    {account?.owned_by ? <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={account.owned_by}
                            />
                        }
                        clickable={false}
                        trigger={account?.owned_by ?
                            <div className='TableTruncateFixedWidth'>
                                <Person
                                    username={`${account.owned_by.first_name} ${account.owned_by.last_name}`}
                                    email={account.owned_by.email}
                                    isInactiveUser={!account.owned_by.active}
                                    popup={false}
                                    title={" "}
                                />
                            </div>
                            : undefined}
                    /> : "Unassigned"}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CO_OWNER) && (
                <Table.Cell>
                    {account?.co_owned_by ? <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={account.co_owned_by}
                            />
                        }
                        clickable={false}
                        trigger={account?.co_owned_by ?
                            <div className='TableTruncateFixedWidth'>
                                <Person
                                    username={`${account.co_owned_by.first_name} ${account.co_owned_by.last_name}`}
                                    email={account.co_owned_by.email}
                                    isInactiveUser={!account.co_owned_by.active}
                                    popup={false}
                                    title={" "}
                                />
                            </div>
                            : undefined}
                    /> : "Unassigned"}
                </Table.Cell>
            )}
            {
                isDyl && !isColumnHidden(KEY_FUND_TYPE) && (
                    <Table.Cell>
                        <Header as='h5'>
                            {StringUtils.capitalize(account.fund_type)}
                        </Header>
                    </Table.Cell>
                )
            }
            {!isColumnHidden(KEY_CUSTOMER_SINCE) && (
                <Table.Cell>
                    <div><b>{account.customer_since && DateTimeUtils.formatEpoch(account.customer_since, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{account.customer_since && DateTimeUtils.formatEpoch(account.customer_since, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CANCELED_ON) && (
                <Table.Cell>
                    <div><b>{account.canceled_on && DateTimeUtils.formatEpoch(account.canceled_on, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{account.canceled_on && DateTimeUtils.formatEpoch(account.canceled_on, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CANCELED_BY) && (
                <Table.Cell>
                    {account.canceled_by && (
                        <Person
                            username={account.canceled_by.username}
                            email={account.canceled_by.email}
                            isInactiveUser={!account.canceled_by.active}
                        />
                    )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_REACTIVATED_ON) && (
                <Table.Cell>
                    <div><b>{account.reactivated_on && DateTimeUtils.formatEpoch(account.reactivated_on, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{account.reactivated_on && DateTimeUtils.formatEpoch(account.reactivated_on, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_REACTIVATED_BY) && (
                <Table.Cell>
                    {account.reactivated_by && (
                        <Person
                            username={account.reactivated_by.username}
                            email={account.reactivated_by.email}
                            isInactiveUser={!account.reactivated_by.active}
                        />
                    )}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_ACCOUNT_STATUS) &&
                <Table.Cell className='Account__status'>
                    {getAccountStatus(account)}
                </Table.Cell>
            }
            {!isColumnHidden(KEY_ACTIONS) &&
                <Table.Cell>
                    {((account.account_type !== 'household' && account.account_type !== 'Master account') || (account.account_type === 'Master account' && parseInt(account.account_status) === 0)) &&
                        <Ref innerRef={ref}>
                            <EllipsisDropdownPortal reference={ref}>
                                {createActionOptions(
                                    false,
                                    account.account_type,
                                    () => { onLinkSelect(account.id); },
                                    null,
                                    () => { onDeleteMasterAccountSelect(actionPopupRef, account.id); }
                                ).map(({ text, icon, userPermission, ...otherProps }, idx) =>
                                    <Ref innerRef={actionPopupRef}>
                                        <RestrictedView type='disabled' userPermission={userPermission}>
                                            <DropdownItem
                                                key={idx}
                                                text={text}
                                                icon={icon}
                                                {...otherProps}
                                            />
                                        </RestrictedView>
                                    </Ref>
                                )}
                            </EllipsisDropdownPortal>
                        </Ref>
                    }
                </Table.Cell>
            }
        </RowComponent>
    );
}
